import { LogPane } from "@/components/LogPane";
import { ErrorComponent, LoadingIndicator } from "@/components/QueryState";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";

export function LogsTab() {
  const params = useParams({ strict: false }) as { containerId?: string };
  const containerId = params.containerId!;
  const [tab, setTab] = useState<"build" | "runtime">("build");

  const { data: listResponse } = useQuery({
    queryKey: ["containers"],
    queryFn: api.containers.list,
  });
  const container = listResponse?.data.find((c) => c.id === containerId);

  // Poll while the container is actively booting or running so new
  // log lines stream in. Stops polling once it's stopped/error to avoid
  // chewing through requests when nothing is happening.
  const isLive = container?.deploymentStatus === "running" || container?.deploymentStatus === "creating" || container?.deploymentStatus === "restarting";

  const logsQuery = useQuery({
    queryKey: ["container-logs", containerId],
    queryFn: () => api.containers.logs(containerId),
    refetchInterval: isLive ? 5000 : false,
  });

  if (logsQuery.isLoading) return <LoadingIndicator />;
  if (logsQuery.isError || !logsQuery.data) return <ErrorComponent error={logsQuery.error ?? "Failed to load logs"} />;

  return (
    <div className="flex flex-1 flex-col min-h-0 p-6 gap-4">
      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as "build" | "runtime")}
        className="flex flex-1 flex-col min-h-0 gap-4"
      >
        <TabsList className="w-fit">
          <TabsTrigger value="build">Build</TabsTrigger>
          <TabsTrigger value="runtime">Runtime</TabsTrigger>
        </TabsList>

        <TabsContent value="build" className="flex-1 min-h-0 flex flex-col">
          <LogPane lines={logsQuery.data.buildLines} isLoading={false} />
        </TabsContent>

        <TabsContent value="runtime" className="flex-1 min-h-0 flex flex-col">
          <LogPane lines={logsQuery.data.runtimeLines} isLoading={false} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
